import { useEffect } from 'react'
import { Eye } from 'lucide-react'
import { useT } from '@/i18n'
import { useDeviceClass } from '../layout/deviceClass'
import { coarsePointer } from '../inventory/selfHit'
import { setHeld, setViewMode, useViewMode } from './firstPerson'

const base = 'flex min-h-11 min-w-11 items-center justify-center rounded-sm border font-ui text-xs uppercase'

/** The key that switches the view (P, point of view): free in the robot's keymap. */
export const VIEW_KEY = 'KeyP'

/** The switch between first person and the overhead view: an eye in the site header on a city's page (P on a keyboard). */
export function ViewToggle() {
  const t = useT(), mode = useViewMode(), first = mode === 'first'
  // P switches the view mid-game too (point of view), never while typing or with a modifier held
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== VIEW_KEY || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return
      const el = e.target as HTMLElement | null
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return
      e.preventDefault()
      setViewMode(first ? 'iso' : 'first')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [first])
  return (
    <button type="button" data-hud data-scout-keep data-testid="view-toggle" aria-pressed={first} aria-keyshortcuts="P" aria-label={first ? t('city.view.toOverhead') : t('city.view.toFirst')} title={`${first ? t('city.view.toOverhead') : t('city.view.toFirst')} (P)`}
      onClick={() => setViewMode(first ? 'iso' : 'first')}
      className={`rounded-sm p-2 hover:text-gold ${first ? 'text-gold' : 'text-bone-muted'}`}>
      <Eye size={18} aria-hidden="true" />
    </button>
  )
}

/** A held pad button: down holds the move, up or cancel lets go. */
function PadButton({ k, label, glyph, source }: { k: 'fwd' | 'back'; label: string; glyph: string; source: string }) {
  const on = (v: boolean) => () => setHeld(source, k, v)
  return (
    <button type="button" aria-label={label} onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); on(true)() }} onPointerUp={on(false)} onPointerCancel={on(false)} onLostPointerCapture={on(false)}
      onContextMenu={(e) => e.preventDefault()}
      className={`${base} h-14 w-14 touch-none select-none border-gold/50 bg-obsidian-600/80 text-lg text-bone active:bg-gold/30`}>{glyph}</button>
  )
}

/** The move pad for touch screens: forward and back (drag the view to look round). */
export function MovePad() {
  const t = useT(), d = useDeviceClass()
  const touch = coarsePointer() || d === 'phone-portrait' || d === 'phone-landscape' || d === 'tablet' || d === 'tesla'
  if (!touch) return null
  return (
    <div data-hud data-testid="move-pad" className="absolute bottom-6 left-4 z-10 flex flex-col gap-2" onPointerDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
      <PadButton k="fwd" source="pad" label={t('city.view.forward')} glyph={'▲'} />
      <PadButton k="back" source="pad" label={t('city.view.backward')} glyph={'▼'} />
    </div>
  )
}
